import { Button } from "@/components/ui/button";
import { MapLocation } from "@/types/location";
import { useLanguage } from "@/contexts/LanguageContext";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";

interface LocationTypesStripProps {
  activeRegion: string;
  activeMediaOwner: string;
  activeMediaKind: string;
  locations: MapLocation[];
  selectedLocations: Set<number>;
  onToggleType: (region: string, type: string) => void;
  onToggleAll: (region: string) => void;
}

export const LocationTypesStrip = ({
  activeRegion,
  activeMediaOwner,
  activeMediaKind,
  locations,
  selectedLocations,
  onToggleType,
  onToggleAll
}: LocationTypesStripProps) => {
  const { t } = useLanguage();

  if (activeRegion === 'all') return null;

  const getLocationTypesForRegion = () => {
    const filteredLocs = locations.filter(loc => {
      const regionMatch = loc.region === activeRegion;
      const mediaOwnerMatch = activeMediaOwner === 'all' ? true : loc.mediaOwner === activeMediaOwner;
      const mediaKindMatch = activeMediaKind === 'all' || 
        loc.mediaKind.toLowerCase() === activeMediaKind.toLowerCase();
      return regionMatch && mediaOwnerMatch && mediaKindMatch;
    });
    return [...new Set(filteredLocs.map(loc => loc.type))];
  };

  const getRegionLocationCount = () => {
    return locations.filter(loc => {
      const regionMatch = loc.region === activeRegion;
      const mediaOwnerMatch = activeMediaOwner === 'all' ? true : loc.mediaOwner === activeMediaOwner;
      const mediaKindMatch = activeMediaKind === 'all' || 
        loc.mediaKind.toLowerCase() === activeMediaKind.toLowerCase();
      return regionMatch && mediaOwnerMatch && mediaKindMatch;
    }).length;
  };

  const getSelectedLocationCount = () => {
    return Array.from(selectedLocations).filter(id => {
      const loc = locations.find(l => l.id === id);
      if (!loc) return false;
      const regionMatch = loc.region === activeRegion;
      const mediaOwnerMatch = activeMediaOwner === 'all' ? true : loc.mediaOwner === activeMediaOwner;
      const mediaKindMatch = activeMediaKind === 'all' || 
        loc.mediaKind.toLowerCase() === activeMediaKind.toLowerCase();
      return regionMatch && mediaOwnerMatch && mediaKindMatch;
    }).length;
  };

  const locationTypes = getLocationTypesForRegion();
  
  if (locationTypes.length === 0) return null;

  return (
    <div className="absolute top-2 left-2 right-2 z-[1000] pointer-events-auto">
      <div className="bg-background/95 backdrop-blur-sm rounded-lg border border-border/50 shadow-lg p-2">
        <ScrollArea className="w-full">
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onToggleAll(activeRegion)}
              className="text-xs whitespace-nowrap bg-primary/10 hover:bg-primary/20 border-primary/30"
            >
              {t('toggleAll')} ({getSelectedLocationCount()}/{getRegionLocationCount()})
            </Button>
            
            <div className="w-px h-6 bg-border/50" />
            
            {locationTypes.map((type) => {
              const typeLocations = locations.filter(loc => {
                const regionMatch = loc.region === activeRegion;
                const typeMatch = loc.type === type;
                const mediaOwnerMatch = activeMediaOwner === 'all' ? true : loc.mediaOwner === activeMediaOwner;
                const mediaKindMatch = activeMediaKind === 'all' || 
                  loc.mediaKind.toLowerCase() === activeMediaKind.toLowerCase();
                return regionMatch && typeMatch && mediaOwnerMatch && mediaKindMatch;
              });
              const selectedTypeCount = typeLocations.filter(loc => selectedLocations.has(loc.id)).length;
              const isAllSelected = selectedTypeCount === typeLocations.length && typeLocations.length > 0;
              
              return (
                <Button
                  key={type}
                  variant={isAllSelected ? "default" : "outline"}
                  size="sm"
                  onClick={() => onToggleType(activeRegion, type)}
                  className={`text-xs whitespace-nowrap ${
                    isAllSelected 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-card hover:bg-accent'
                  }`}
                >
                  {type} ({selectedTypeCount}/{typeLocations.length})
                </Button>
              );
            })}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>
    </div>
  );
};
